// Shared dropdown-with-checkboxes multiselect — the Sessions tab's Outcomes
// popover, promoted to a component. One trigger button ("Label · N ▾") that
// highlights when a selection is active, opening a checkbox menu with optional
// per-option count badges. Used by every metric panel's multi-value control
// (outcomes, complexity, issue type, facet filters, ops error filter) and the
// scope bar's repo picker, so multi-select means ONE thing everywhere.
//
// Reuses the .flt-pop / .flt-menu CSS (styles.css) verbatim — same chrome the
// Sessions bar already shows, so nothing new to learn.
//
// Toggling a box calls onChange with the new selection and updates the badge IN
// PLACE — the menu stays open, so picking three values is three clicks, not
// three open-the-menu dances. Callers must NOT rebuild their controls inside
// onChange (that would close the menu); reload data only.
import { esc, num } from './core'

export interface MsOption {
  value: string
  label: string
  /** Optional per-option count badge (e.g. sessions with this outcome). */
  count?: number | null
}

export interface MsSpec {
  /** DOM id prefix — must be unique per control instance on the page. */
  id: string
  options: MsOption[]
  /** Currently checked values. */
  selected: string[]
  /**
   * Badge + highlight count for the trigger. Defaults to selected.length; a
   * control whose empty-string state means "all" (complexity, issue type)
   * passes 0 when unfiltered so the button only lights up for a REAL filter.
   */
  badge?: (selected: string[]) => number
  /** Refuse to uncheck the last box (cost-artifact convention). */
  minOne?: boolean
  /** Menu body when there are no options at all. */
  emptyText?: string
  onChange: (selected: string[]) => void
}

// One document-level closer for every instance: any open menu closes on a click
// outside its own wrapper (same pattern the Sessions bar wires by hand).
var closerWired = false
function ensureCloser() {
  if (closerWired) return
  closerWired = true
  document.addEventListener('mousedown', function (e) {
    Array.prototype.forEach.call(document.querySelectorAll('.ms-wrap .flt-menu.on'), function (menu) {
      var wrap = (menu as HTMLElement).parentElement
      if (wrap && wrap.contains(e.target as Node)) return
      ;(menu as HTMLElement).classList.remove('on')
    })
  })
}

/** The trigger + (empty) menu markup. Render this into the control row, then
 * call msWire with the same id once it's in the DOM. */
export function msHtml(id: string, label: string, badgeCount: number): string {
  return '<span class="flt-pop ms-wrap" id="' + esc(id) + '-wrap">' +
    '<button type="button" class="flt-pop-btn' + (badgeCount ? ' on' : '') + '" id="' + esc(id) + '-btn">' + esc(label) +
      '<span class="flt-pop-cnt" id="' + esc(id) + '-cnt">' + (badgeCount ? String(badgeCount) : '') + '</span>' +
      '<span class="flt-pop-car">▾</span></button>' +
    '<div class="flt-menu" id="' + esc(id) + '-menu"></div>' +
  '</span>'
}

/** Build the menu and wire open/close + change handling. */
export function msWire(spec: MsSpec): void {
  ensureCloser()
  var btn = document.getElementById(spec.id + '-btn')
  var menu = document.getElementById(spec.id + '-menu')
  if (!btn || !menu) return
  if (!spec.options.length) {
    menu.innerHTML = '<div class="flt-menu-empty">' + esc(spec.emptyText || 'No values yet.') + '</div>'
  } else {
    menu.innerHTML = spec.options.map(function (o) {
      var on = spec.selected.indexOf(o.value) >= 0
      return '<label class="flt-menu-item"><input type="checkbox" value="' + esc(o.value) + '"' + (on ? ' checked' : '') + '/>' +
        '<span class="flt-menu-tx">' + esc(o.label) + '</span>' +
        (o.count != null ? '<span class="flt-menu-n">' + num(o.count) + '</span>' : '') +
      '</label>'
    }).join('')
  }
  btn.onclick = function () { menu!.classList.toggle('on') }
  Array.prototype.forEach.call(menu.querySelectorAll('input'), function (cb: HTMLInputElement) {
    cb.onchange = function () {
      var vals: string[] = []
      Array.prototype.forEach.call(menu!.querySelectorAll('input:checked'), function (x: HTMLInputElement) { vals.push(x.value) })
      if (spec.minOne && !vals.length) { cb.checked = true; return } // keep at least one
      var n = spec.badge ? spec.badge(vals) : vals.length
      var cnt = document.getElementById(spec.id + '-cnt')
      if (cnt) cnt.textContent = n ? String(n) : ''
      btn!.classList.toggle('on', n > 0)
      spec.onChange(vals)
    }
  })
}
